/*
  Case studies v2: shared behaviour.
  <cs-section> wraps every sections/cs-*.liquid section. It reveals [data-cs-reveal]
  children once as they scroll into view and cleans up when the theme editor
  re-renders or removes the section (disconnectedCallback). A [data-cs-fit] child
  gets --cs-fit-top = its distance from the top of the document, so CSS can size it
  to "viewport minus whatever sits above it" (header, announcement bar).

  <cs-video> lazily plays a decorative background video: the src is set only when the
  element is on screen, and never for reduced-motion or Save-Data visitors (they keep
  the poster). It pauses while off screen or when the tab is hidden.
*/
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = !!(navigator.connection && navigator.connection.saveData);

  if (!customElements.get('cs-section')) {
    class CsSection extends HTMLElement {
      connectedCallback() {
        this.fitEls = [...this.querySelectorAll('[data-cs-fit]')];
        if (this.fitEls.length) {
          this.ac = new AbortController();
          const fit = () =>
            this.fitEls.forEach((el) => {
              const top = el.getBoundingClientRect().top + window.scrollY;
              el.style.setProperty('--cs-fit-top', `${Math.max(0, Math.round(top))}px`);
            });
          fit();
          window.addEventListener('resize', fit, { signal: this.ac.signal, passive: true });
          window.addEventListener('load', fit, { signal: this.ac.signal, once: true });
        }

        const items = this.querySelectorAll('[data-cs-reveal]');
        if (!items.length) return;

        const designMode = window.Shopify && window.Shopify.designMode;
        if (designMode || reduceMotion.matches || !('IntersectionObserver' in window)) {
          items.forEach((el) => el.classList.add('is-in'));
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              this.observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        );
        items.forEach((el) => this.observer.observe(el));
      }

      disconnectedCallback() {
        if (this.ac) this.ac.abort();
        if (this.observer) this.observer.disconnect();
        this.observer = null;
      }
    }
    customElements.define('cs-section', CsSection);
  }

  if (!customElements.get('cs-video')) {
    class CsVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video[data-src]');
        if (!this.video) return;
        this.inView = false;
        this.ac = new AbortController();
        const opts = { signal: this.ac.signal };
        // Apply a social tile's offset once, after its duration is known. Resuming
        // an off-screen tile keeps its position instead of restarting the loop.
        this.startPending = this.video.hasAttribute('data-start-fraction');
        this.video.addEventListener('loadeddata', () => {
          if (this.startPending && Number.isFinite(this.video.duration) && this.video.duration > 0) {
            const fraction = Number(this.video.dataset.startFraction);
            if (Number.isFinite(fraction) && fraction > 0) {
              this.startSeeking = true;
              this.video.currentTime = Math.max(0, Math.min(0.99, fraction)) * this.video.duration;
            }
            this.startPending = false;
          }
          this.sync();
        }, opts);
        this.video.addEventListener('seeked', () => {
          this.startSeeking = false;
          this.sync();
        }, opts);
        this.video.addEventListener('playing', () => this.classList.add('is-playing'), opts);
        document.addEventListener('visibilitychange', () => this.sync(), opts);
        reduceMotion.addEventListener('change', () => this.sync(), opts);
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(([e]) => {
            this.inView = e.isIntersecting;
            this.sync();
          });
          this.io.observe(this);
        } else {
          this.inView = true;
          this.sync();
        }
      }

      disconnectedCallback() {
        if (this.ac) this.ac.abort();
        if (this.io) this.io.disconnect();
        if (this.video) this.video.pause();
      }

      sync() {
        const v = this.video;
        const allowed = this.inView && !document.hidden && !reduceMotion.matches && !saveData;
        if (allowed) {
          if (!v.getAttribute('src')) {
            if (this.startPending) v.preload = 'auto';
            const mobileSrc = v.dataset.srcMobile;
            const src = mobileSrc && window.matchMedia('(max-width: 749px)').matches ? mobileSrc : v.dataset.src;
            v.setAttribute('src', src);
          }
          if (this.startPending || this.startSeeking) return;
          const p = v.play();
          if (p && p.catch) p.catch(() => this.classList.remove('is-playing'));
        } else {
          if (!v.paused) v.pause();
          if (reduceMotion.matches) this.classList.remove('is-playing');
        }
      }
    }
    customElements.define('cs-video', CsVideo);
  }
})();
